// @docs:
//   - docs/core/operations/DEPARTURE_PLANNER.md
//
// Admin › Departure planner — times on the departure port's clock, send hours, and
// a passage named by its ends, and the scheduler's check results in plain words. Pure
// functions, so the page's tests can pin them.

import type { LatLon } from '../../services/api';

const PARTS = (tz: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

function wallParts(ms: number, tz: string) {
  const parts = PARTS(tz).formatToParts(new Date(ms));
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value);
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute'), s: get('second') };
}

/** How far the zone's wall clock is ahead of UTC at `ms`. */
function offsetMs(ms: number, tz: string): number {
  const p = wallParts(ms, tz);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
}

/** A datetime-local value ("2026-10-03T08:00") read on `tz`'s clock, as ISO UTC. */
export function zonedInputToIso(local: string, tz: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return null;
  try {
    const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    // Twice, so a guess on the far side of a daylight-saving change settles.
    let t = wall - offsetMs(wall, tz);
    t = wall - offsetMs(t, tz);
    return new Date(t).toISOString();
  } catch {
    return null;
  }
}

/** An ISO time as a datetime-local value on `tz`'s clock; '' if it cannot be shown. */
export function isoToZonedInput(iso: string, tz: string): string {
  try {
    const p = wallParts(Date.parse(iso), tz);
    const two = (n: number) => String(n).padStart(2, '0');
    return `${p.y}-${two(p.mo)}-${two(p.d)}T${two(p.h)}:${two(p.mi)}`;
  } catch {
    return '';
  }
}

/** "8, 20" → [8, 20]; null unless every entry is a different whole hour 0–23. */
export function parseHours(s: string): number[] | null {
  const bits = s.split(/[\s,]+/).filter(Boolean);
  if (!bits.length || !bits.every((b) => /^\d{1,2}$/.test(b))) return null;
  const hours = bits.map(Number);
  if (hours.some((h) => h > 23) || new Set(hours).size !== hours.length) return null;
  return hours.sort((a, b) => a - b);
}

const deg = ([lat, lon]: LatLon) => `${Math.abs(lat).toFixed(2)}°${lat < 0 ? 'S' : 'N'} ${Math.abs(lon).toFixed(2)}°${lon < 0 ? 'W' : 'E'}`;

/** "23.40°S 166.90°E → 34.95°S 174.60°E, via 2 waypoints" */
export function routeText(w: { start: LatLon; end: LatLon; via: LatLon[] }): string {
  const via = w.via.length ? `, via ${w.via.length} waypoint${w.via.length > 1 ? 's' : ''}` : '';
  return `${deg(w.start)} → ${deg(w.end)}${via}`;
}

/** The scheduler's check_result (vortex-api services/departurePlanner.ts). */
export interface DepartureCheck {
  ok?: boolean;
  unknown?: string[];
  attempts?: number;
  hazardUnstated?: boolean;
  fallback?: boolean;
  reason?: string;
  scanAttempts?: number;
  /** "Nothing can be assessed yet" was said already: held without an email. */
  suppressed?: boolean;
}

/** A verdict in words, as the email says it (vortex-api lib/departureVerdicts.ts verdictWords). Old digests' words too. */
export function verdictWords(v: string | undefined): string {
  switch (v) {
    case 'possible': return 'possible';
    case 'possible_with_fill': return 'possible, partly on filled-in forecast';
    case 'early_look': return 'early look only';
    case 'over_comfort': return 'over the comfort limits';
    case 'over_limits': case 'not possible': return 'over the limits';
    case 'models_disagree': return 'models disagree';
    case 'not_covered': case 'no forecast': return 'not reachable yet';
    case 'blocked': return 'blocked';
    default: return v ?? '—';
  }
}

interface DigestDep { depart?: string; verdict?: string; why?: string | null; passageText?: string | null; arrive?: string | null; fillLine?: string | null }
interface Digest {
  headline?: { subject?: string; sentence?: string; nextChange?: string | null };
  best?: { depart?: string; passageText?: string; arrive?: string; fillLine?: string | null } | null;
  groups?: { from?: string; to?: string; count?: number; verdict?: string; why?: string }[];
  departures?: DigestDep[];
  models?: { model?: string; run?: string }[];
  tailFillLine?: string;
}

/** What the digest says, line by line, for the reviewer to check the email against. */
export interface DigestFacts {
  answer: string | null;
  best: string | null;
  failures: { when: string; verdict: string; why: string }[];
  fills: string[];
  runs: string | null;
}

/**
 * The facts behind an assessment, as plain lines: the answer, the best departure, each run of
 * departures that fail and why (in the engine's words), every forecast fill, and the runs used.
 * An older digest without groups lists each departure that is not possible. Null without a digest.
 */
export function digestFacts(raw: unknown): DigestFacts | null {
  const d = (raw ?? null) as Digest | null;
  if (!d || !Array.isArray(d.departures)) return null;
  const go = (v?: string) => v === 'possible' || v === 'possible_with_fill';
  const failures = Array.isArray(d.groups)
    ? d.groups.map((g) => ({
        when: g.count && g.count > 1 ? `${g.from} to ${g.to} (${g.count})` : g.from ?? '—',
        verdict: verdictWords(g.verdict),
        why: g.why ?? '',
      }))
    : d.departures.filter((x) => !go(x.verdict)).map((x) => ({ when: x.depart ?? '—', verdict: verdictWords(x.verdict), why: x.why ?? '' }));
  const fills = [...new Set(d.departures.map((x) => (x.fillLine ? `${x.depart}: ${x.fillLine}` : null)).filter((x): x is string => !!x))];
  const b = d.best;
  return {
    answer: d.headline?.sentence ?? null,
    best: b?.depart ? `${b.depart}${b.passageText ? `, ${b.passageText}` : ''}${b.arrive ? `, arriving ${b.arrive}` : ''}` : null,
    failures,
    fills,
    runs: d.models?.length
      ? `${d.models.map((m) => `${m.model} ${m.run}`).join(', ')}${d.headline?.nextChange ? `; a new run may change this from ${d.headline.nextChange}` : ''}${d.tailFillLine ? `. ${d.tailFillLine}` : ''}`
      : null,
  };
}

/**
 * "When a forecast ends before you arrive": the choices, each in one sentence (Tony's wording).
 * Typical weather ('climatology') is not offered: the engine has none yet (vortex-routing
 * NoClimatology). A stored 'longest' or 'climatology' runs as ECMWF (vortex-api engineTailFill).
 */
export const TAIL_FILLS = [
  { id: 'ecmwf', title: 'Finish with ECMWF (recommended)', sentence: 'A model that ends early is completed with ECMWF, and with GFS where ECMWF ends too; the email says which model, which weather and how many hours.' },
  { id: 'gfs', title: 'Finish with GFS', sentence: 'A model that ends early is completed with GFS, which usually looks furthest ahead.' },
  { id: 'strict', title: 'Only use forecasts that reach all the way', sentence: "A model that runs out before arrival is left out of that departure's assessment." },
] as const;
export type TailFillId = (typeof TAIL_FILLS)[number]['id'];

/** What the checker found, for the reviewer; null when there is nothing to say. */
export function checkText(raw: unknown): string | null {
  const c = (raw ?? {}) as DepartureCheck;
  if (typeof c.ok !== 'boolean') return (c.scanAttempts ?? 1) > 1 ? 'The scan was sent twice: the first attempt failed.' : null;
  if (c.suppressed) return `Not sent: ${c.reason ?? 'nothing can be assessed yet, as the last email said.'} Approve it to send it anyway.`;
  if (c.fallback) return `Written without AI: ${c.reason ?? 'no reason given.'} Read it before approving.`;
  if (c.ok) return 'Checked: every number and time is from the scan.';
  const why: string[] = [];
  if (c.unknown?.length) {
    const drafts = (c.attempts ?? 1) > 1 ? `, after ${c.attempts} drafts` : '';
    why.push(`the draft has numbers or times that are not in the scan (${c.unknown.join(', ')})${drafts}. Correct or remove them before approving.`);
  }
  if (c.hazardUnstated) why.push('a departure is rated Hazardous and the draft does not say so.');
  return `Not sent automatically: ${why.join(' Also, ') || 'the check did not pass.'}`;
}
